import {
	type BackendMessageStageInput,
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--post-office-team/rovo-nudge';
import type { MessageTemplateContext } from '../../../index';
import { NudgeDefinition } from '@post-office/placements--post-office-team/rovo-nudge/nudge-definition';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import { useClickedRovoNudgeDismiss } from '@post-office/rovo-nudge-util';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

const nudgeTitleMessage = defineMessage({
	defaultMessage:
		'You have {numberOfSuggestions} work item {numberOfSuggestions, plural, one {update} other {updates}}',
	description: 'The title of the nudge shown to users when there are work item updates available.',
	id: 'ai-rovo.actions-software-backlog.nudge-title.nonfinal',
});

const nudgeDescriptionMessage = defineMessage({
	defaultMessage:
		'We have identified {numberOfSuggestions} work item {numberOfSuggestions, plural, one {update} other {updates}}',
	description:
		'The description of the nudge shown to users when there are work item updates available.',
	id: 'ai-rovo.actions-software-backlog.nudge-description.nonfinal',
});

const nudgeIconLabelMessage = defineMessage({
	defaultMessage: 'Work items icon',
	description: 'Label for the work items icon used in the nudge.',
	id: 'ai-rovo.actions-software-backlog.nudge-icon-label.nonfinal',
});

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation<MessageTemplateContext>(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: noValidation<MessageTemplateContext>(),
		stage: async ({
			message,
		}: BackendMessageStageInput<MessageTemplateContext>): Promise<MessageTemplateContext> =>
			message.context,
	},
}));

const nudgeContext = {
	id: 'jira-board-backlog-update-work-suggestions',
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageTemplateContext>(),
		Component: ({
			conversationChannelId,
			numberOfSuggestions,
			projectKey,
		}: MessageTemplateContext) => {
			useClickedRovoNudgeDismiss({ nudgeId: nudgeContext.id });
			const publish = usePublish('ai-mate');
			const { formatMessage } = useIntl();

			const icon = useCallback(
				() => <WorkItemsIcon size="medium" label={formatMessage(nudgeIconLabelMessage)} />,
				[],
			);

			const nudgeData = {
				id: `jira-board-backlog-update-work-suggestions-rovo-nudge-${conversationChannelId}`,
				onClick: () => {
					publish({
						type: 'chat-open' as const,
						product: 'jira' as const,
						data: {
							channelId: conversationChannelId,
							...(fg('jira_board_backlog_project_key_context') && projectKey
								? { aiFeatureContext: { projectKey } }
								: {}),
						},
						openChatMode: 'mini-modal' as const,
						source: 'project-board-backlog',
					});
				},
			};

			return (
				<NudgeDefinition
					title={formatMessage(nudgeTitleMessage, { numberOfSuggestions })}
					description={formatMessage(nudgeDescriptionMessage, { numberOfSuggestions })}
					icon={icon}
					handleType={HandleActionType.Base}
					nudgeContext={nudgeContext}
					{...nudgeData}
				/>
			);
		},
	},
});
